import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import type DataMapperMappingStore from "./DataMapperMappingStore";
import type DataMapperProfileStateStore from "../DataMapperProfileStateStore";
import { DATA_MAPPER_STEP } from "../DataMapperProfileStateStore";
import { DATA_MAPPER_ROUTE } from "../shared/editorRoutes";

const mappingStore = (rootStore: RootStore) => rootStore.dataMapperMappingViewStore as DataMapperMappingStore;
const stateStore = (rootStore: RootStore) =>
  rootStore.dataMapperProfileStateViewStore as DataMapperProfileStateStore;

/** Read the profile's saved selection first (it may name the config), then load the draft. */
const enter = (step: number) => ({ rootStore }: { rootStore: RootStore }) => {
  void (async () => {
    await stateStore(rootStore)?.enterStep(step);
    await mappingStore(rootStore)?.load();
  })();
};

const exit = ({ rootStore }: { rootStore: RootStore }) => {
  void stateStore(rootStore)?.flush();
  // Keeps the draft: Map ↔ Transform must not lose unsaved edits.
  mappingStore(rootStore)?.reset();
};

/**
 * Data Mapper → Map and → Transform. One draft (DataMapperMappingStore) backs
 * both. Gated like the Profiles route: the store checks `canUseDataMapper`
 * before calling, and the API refuses viewers on every data-mapper service.
 */
const dataMapperMappingRoutes: RouteType[] = [
  {
    name: DATA_MAPPER_ROUTE.map,
    path: "/data-mapper/map",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Map Fields",
        enableRefreshButton: true,
        component: () => import("../shared/DataMapperEditor.Header"),
      },
      body: {
        component: () => import("./DataMapperMap.Body"),
        grayBackground: true,
      },
    },
    group: "Data Mapper",
    searchAliases: ["data mapper map", "field mapping", "mapping configuration"],
    requiresAuth: true,
    requiresPermission: false,
    onEnter: enter(DATA_MAPPER_STEP.map),
    onExit: exit,
  },
  {
    name: DATA_MAPPER_ROUTE.transform,
    path: "/data-mapper/transform",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Transforms",
        enableRefreshButton: true,
        component: () => import("../shared/DataMapperEditor.Header"),
      },
      body: {
        component: () => import("./DataMapperTransform.Body"),
        grayBackground: true,
      },
    },
    group: "Data Mapper",
    searchAliases: ["data mapper transform", "transform chain", "value lookup"],
    requiresAuth: true,
    requiresPermission: false,
    onEnter: enter(DATA_MAPPER_STEP.transform),
    onExit: exit,
  },
];

export default dataMapperMappingRoutes;
